import { Grid, InputAdornment } from "@mui/material";
import dayjs from "dayjs";
import _ from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../framework/Button";
import Checkbox from "../../framework/Checkbox";
import ChipInputSelect from "../../framework/ChipInputSelect";
import DatePicker from "../../framework/date/DatePicker";
import GlobalContext from "../../framework/GlobalContext";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { displayToast } from "../../framework/toast/toastUtils";
import { listBranchGroup } from "../../services/branch-group-services";
import { fetchBranches } from "../../services/branch-service";
import { addDoctorCommission } from "../../services/doctor-commission";
import { searchManufacturerGroup } from "../../services/manufacturer-group-services";
import { focusCell } from "../../utils/gridUtils";
import { genericMedicineCategories, doctorTypes } from "../../utils/constant";
import classes from "./DoctorCommissionProgramActors.module.css";
import { initial_state_doctor_commission } from "./DoctorCommissionProgramConstants";
import DoctorCommissionProgramContext from "./DoctorCommisionProgramContext";
import {
  createDoctorCommissionProgramFormData,
  createDoctorCommissionProgramItemFormData,
} from "./DoctorCommisionProgramUtils";
const DoctorCommissionProgramActors = (props) => {
  const { setCurrentComponent, setShowLoading, showLoading } =
    useContext(GlobalContext);
  const {
    doctorCommissionProgramRef,
    doctorCommissionProgramStartDateRef,
    saveDoctorCommissionProgramRef,
    doctorCommissionProgramGridRef,
    doctorCommissionProgramEndDateRef,
  } = useContext(DoctorCommissionProgramContext);
  const branchRef = useRef("");
  const manufacturerGroupRef = useRef(null);
  const genericMedicineCategoryRef = useRef(0);
  const doctorTypeRef = useRef(null);
  const branchResetKeyRef = useRef(0);
  const [branches, setBranches] = useState([]);
  const [manufacturerGroupOptions, setManufacturerGroupOptions] = useState([]);
  const dispatch = useDispatch();
  const {
    doctorCommissionProgramRowData,
    actors: {
      name,
      start,
      end,
      doctorCommissionId,
      branch,
      manufacturerGroup,
      medicineForm = [],
      doctorType,
    },
    gridColumns,
  } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );
  const resetDoctorCommissionProgram = useCallback(() => {
    branchResetKeyRef.current += 1;
    genericMedicineCategoryRef.current += 1;
    const newDoctorCommissionProgram = {
      min: 0,
      max: 0,
      commissionPercentage: 0,
    };
    const newDoctorCommissionProgramData = [newDoctorCommissionProgram];
    dispatch({
      type: "reset_active_tab",
      payload: {
        ...initial_state_doctor_commission,
        gridColumns,
        doctorCommissionProgramRowData: newDoctorCommissionProgramData,
      },
    });

    setTimeout(() => {
      focusCell(
        doctorCommissionProgramGridRef.current.api,
        0,
        doctorCommissionProgramGridRef.current.columnApi.getColumn("category")
      );
    }, 100);
  }, [doctorCommissionProgramGridRef, dispatch, gridColumns]);

  const updateActorStates = (payload) => {
    dispatch({ type: "update_doctor_commission_program_actors", payload });
  };
  const debounceUpdateActors = _.debounce(updateActorStates, 1);

  const fetchBranchesWithBranchGroup = useCallback(async () => {
    try {
      const _branch = await fetchBranches();
      const _branchGroup = await listBranchGroup();
      const mergedBranches = [
        ...(_branchGroup?.result?.rows || []),
        ...(_branch?.result?.rows || []),
      ];
      setBranches(mergedBranches || []);
      dispatch({
        type: "update_doctor_commission_program_actors",
        payload: {
          allBranches: mergedBranches || [],
        },
      });
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [dispatch]);

  const fetchManufacturerGroups = useCallback(async (searchText = "") => {
    try {
      const payload = {
        filters: [
          {
            fieldName: "name",
            fieldType: { type: "string", operation: "like" },
            fieldValue: searchText,
          },
        ],
        orderBy: [{ fieldName: "name", orderType: "asc" }],
      };
      const resp = await searchManufacturerGroup(payload);
      const options = (resp || []).map((g) => ({
        id: g.manufacturerGroupId,
        manufacturerGroupId: g.manufacturerGroupId,
        name: g.name,
        label: g.name,
      }));
      setManufacturerGroupOptions(options);
    } catch (error) {
      console.error("Error fetching manufacturer groups:", error);
    }
  }, []);

  const saveNewDoctorCommissionProgram = useCallback(async () => {
    setShowLoading(true);
    let saveDoctorCommissionProgramItems = [];
    let saveDoctorCommissionProgramData = {};
    doctorCommissionProgramRowData.forEach((item) => {
      saveDoctorCommissionProgramItems.push(
        createDoctorCommissionProgramItemFormData(item)
      );
    });
    const selectedBranchIds = (branch || []).reduce((acc, b) => {
      if (b?.branchCode) {
        acc.push(...b.branchCode);
      } else if (b?.code) {
        acc.push(b.code);
      }
      return acc;
    }, []);
    const uniqueBranchIds = [...new Set(selectedBranchIds)];
    saveDoctorCommissionProgramData = createDoctorCommissionProgramFormData({
      doctorCommissionName: name,
      startDate: start,
      endDate: end,
      doctorCommissionDetails: saveDoctorCommissionProgramItems,
      manufacturerGroupId: manufacturerGroup?.id,
      medicineForm: medicineForm?.map((category) => category?.id),
      doctorType: doctorType?.id,
    });
    const resp = await addDoctorCommission({
      ...saveDoctorCommissionProgramData,
      branchCode: uniqueBranchIds,
      ...(doctorCommissionId && { doctorCommissionId }),
    });
    if (resp?.status === "success") {
      displayToast({
        type: "success",
        title: "Doctor Commission Program Saved.",
      });
      resetDoctorCommissionProgram();
    }
    setShowLoading(false);
  }, [
    setShowLoading,
    doctorCommissionProgramRowData,
    branch,
    name,
    start,
    end,
    resetDoctorCommissionProgram,
    doctorCommissionId,
    manufacturerGroup,
    medicineForm,
    doctorType,
  ]);

  const validateForm = useCallback(() => {
    let isValid = true;

    // Validate Manufacturer Group
    if (!manufacturerGroup) {
      displayToast({
        type: "error",
        title: "Please select manufacturer group.",
      });
      try {
        manufacturerGroupRef.current?.focus?.();
      } catch (_) {}
      return false;
    }

    // Validate Doctor Type
    if (!doctorType) {
      displayToast({
        type: "error",
        title: "Please select doctor type.",
      });
      try {
        doctorTypeRef.current?.focus?.();
      } catch (_) {}
      return false;
    }

    // Validate Program Name
    if (!name) {
      displayToast({
        type: "error",
        title: "Please enter doctor commission program name.",
      });
      doctorCommissionProgramRef.current.focus({ preventScroll: false });
      return false;
    }

    // Validate Start Date
    if (!start || start.length < 10) {
      displayToast({
        type: "error",
        title: "Please enter valid start date",
      });
      doctorCommissionProgramStartDateRef.current.focus({
        preventScroll: false,
      });
      return false;
    }

    // Validate End Date
    if (!end || end.length < 10) {
      displayToast({
        type: "error",
        title: "Please enter valid end date",
      });
      doctorCommissionProgramEndDateRef.current.focus({ preventScroll: false });
      return false;
    }

    if (start && end && dayjs(start, "DD/MM/YYYY") > dayjs(end, "DD/MM/YYYY")) {
      displayToast({
        type: "error",
        title: "End date cannot be earlier than start date",
      });
      doctorCommissionProgramEndDateRef.current.focus({ preventScroll: false });
      return false;
    }

     const manufacturerGroupId = manufacturerGroup?.id || manufacturerGroup?.manufacturerGroupId;
    if (manufacturerGroupId && manufacturerGroupId !== "1ON") {
       if (!medicineForm || !Array.isArray(medicineForm) || medicineForm.length === 0) {
        displayToast({
          type: "error",
          title: "Please select mode.",
        });
        try {
          genericMedicineCategoryRef.current?.focus?.();
        } catch (_) {}
        return false;
      }
    }

    if (!Array.isArray(branch) || branch.length === 0) {
      displayToast({
        type: "error",
        title: "Please select at least one branch.",
      });
      try {
        branchRef.current?.focus?.();
      } catch (_) {}
      return false;
    }

    // Validate grid rows - min and max values
    for (
      let index = 0;
      index < doctorCommissionProgramRowData.length;
      index++
    ) {
      const row = doctorCommissionProgramRowData[index];
      const { min, max, commissionPercentage } = row;

      // Validate that min, max, and discount are numbers
      if (
        typeof min !== "number" ||
        typeof max !== "number" ||
        typeof commissionPercentage !== "number"
      ) {
        displayToast({
          type: "error",
          title: "Please enter valid min, max and discount.",
        });
        focusCell(
          doctorCommissionProgramGridRef.current.api,
          index,
          doctorCommissionProgramGridRef.current.columnApi.getColumn("min")
        );
        isValid = false;
        return false;
      }
      // Validate that min of second row onwards is greater than max of previous row
      if (index > 0) {
        const previousRow = doctorCommissionProgramRowData[index - 1];
        if (
          previousRow &&
          previousRow.max !== undefined &&
          previousRow.max !== null
        ) {
          if (parseInt(min) !== parseInt(previousRow.max) + 1) {
            displayToast({
              type: "error",
              title: "Min value must be (previous row's max + 1).",
            });
            focusCell(
              doctorCommissionProgramGridRef.current.api,
              index,
              doctorCommissionProgramGridRef.current.columnApi.getColumn("min")
            );
            isValid = false;
            return false;
          }
        }
      }
      // Validate that min < max in the same row
      if (max <= min) {
        displayToast({
          type: "error",
          title: "Max value must be greater than min value.",
        });
        focusCell(
          doctorCommissionProgramGridRef.current.api,
          index,
          doctorCommissionProgramGridRef.current.columnApi.getColumn("max")
        );
        isValid = false;
        return false;
      }
    }

    return isValid;
  }, [
    start,
    end,
    name,
    doctorCommissionProgramRowData,
    doctorCommissionProgramStartDateRef,
    doctorCommissionProgramEndDateRef,
    doctorCommissionProgramRef,
    doctorCommissionProgramGridRef,
    branch,
    manufacturerGroup,
    doctorType,
    manufacturerGroupRef,
    doctorTypeRef,
    medicineForm,
    genericMedicineCategoryRef,
  ]);

  const saveClickHandler = useCallback(() => {
    if (validateForm()) {
      saveNewDoctorCommissionProgram();
    }
  }, [saveNewDoctorCommissionProgram, validateForm]);

  useEffect(() => {
    doctorCommissionProgramRef.current.value = name;
  }, [name, doctorCommissionProgramRef]);

  useEffect(() => {
    fetchBranchesWithBranchGroup();
  }, [fetchBranchesWithBranchGroup]);

  return (
    <>
      <div style={{ height: "100%", padding: "5px 0", width: "100%" }}>
        <Grid sx={{ marginTop: 0 }} container spacing={1.25} columns={12}>
          <Grid item xs={3}>
            <SelectInput
              id="doctor_commission_program_manufacturer_group"
              label="Manufacturer Group"
              ref={manufacturerGroupRef}
              key={manufacturerGroup}
              options={manufacturerGroupOptions}
              value={manufacturerGroup}
              openOnFocus={true}
              getOptionLabel={(option) => option?.name || option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option?.manufacturerGroupId === value?.manufacturerGroupId ||
                option?.id === value?.id
              }
              onFocus={() => {
                setCurrentComponent(
                  "doctor_commission_program_manufacturer_group"
                );
                fetchManufacturerGroups("");
              }}
              onChange={(event, option) => {
                debounceUpdateActors({ manufacturerGroup: option });
              }}
              onInputChange={(e, value) => {
                if (!value) {
                  debounceUpdateActors({ manufacturerGroup: null });
                }
                const newValue = e?.target?.value || "";
                fetchManufacturerGroups(newValue);
              }}
              required={true}
              noOptionsText="Search Manufacturer Group"
              markers={JSON.stringify({
                altUp: "grid_doctor_commission_program",
                up: "grid_doctor_commission_program",
                left: "doctor_commission_program_manufacturer_group",
                right: "doctor_commission_program_doctor_type",
                down: "doctor_commission_program_generic_medicine_category",
              })}
            />
          </Grid>
          <Grid item xs={2}>
            <SelectInput
              id="doctor_commission_program_doctor_type"
              label="Doctor Type"
              ref={doctorTypeRef}
              key={doctorType}
              options={doctorTypes}
              value={doctorType}
              getOptionLabel={(option) => option?.label || option?.id || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onFocus={() => {
                setCurrentComponent("doctor_commission_program_doctor_type");
              }}
              onChange={(event, option) => {
                debounceUpdateActors({ doctorType: option });
              }}
              required={true}
              markers={JSON.stringify({
                altUp: "grid_doctor_commission_program",
                up: "grid_doctor_commission_program",
                left: "doctor_commission_program_manufacturer_group",
                right: "pp_date_picker_doctor_commission_program_start_date",
                down: "doctor_commission_program_generic_medicine_category",
              })}
            />
          </Grid>
          <Grid item xs={1.8}>
            <DatePicker
              id="pp_date_picker_doctor_commission_program_start_date"
              ref={doctorCommissionProgramStartDateRef}
              label="Start Date"
              disablePast={true}
              value={dayjs(start, "DD/MM/YYYY")}
              onFocus={() =>
                setCurrentComponent(
                  "pp_date_picker_doctor_commission_program_start_date"
                )
              }
              onChange={(value, { validationError }) => {
                if (!value) {
                  debounceUpdateActors({
                    start: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                if (validationError === "disablePast") {
                  displayToast({
                    type: "error",
                    title: "Past date is not allowed",
                  });
                  debounceUpdateActors({
                    start: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                debounceUpdateActors({ start: value.format("DD/MM/YYYY") });
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  altUp: "grid_doctor_commission_program",
                  up: "grid_doctor_commission_program",
                  left: "doctor_commission_program_doctor_type",
                  right: "pp_date_picker_doctor_commission_program_end_date",
                  down: "doctor_commission_branch",
                }),
              }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <DatePicker
              id="pp_date_picker_doctor_commission_program_end_date"
              inputRef={doctorCommissionProgramEndDateRef}
              label="End Date"
              disablePast={true}
              value={dayjs(end, "DD/MM/YYYY")}
              onFocus={() =>
                setCurrentComponent(
                  "pp_date_picker_doctor_commission_program_end_date"
                )
              }
              onChange={(value, { validationError }) => {
                if (!value) {
                  debounceUpdateActors({
                    end: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                if (validationError === "disablePast") {
                  displayToast({
                    type: "error",
                    title: "Past date is not allowed",
                  });
                  debounceUpdateActors({
                    end: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                debounceUpdateActors({ end: value.format("DD/MM/YYYY") });
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  altUp: "grid_doctor_commission_program",
                  up: "grid_doctor_commission_program",
                  left: "pp_date_picker_doctor_commission_program_start_date",
                  right: "doctor_commission_program_name",
                  down: "doctor_commission_branch",
                }),
              }}
            />
          </Grid>
          <Grid item xs={3.3}>
            <TextInput
              id="doctor_commission_program_name"
              label="Program Name"
              ref={doctorCommissionProgramRef}
              placeholder="Type here..."
              value={name}
              required={true}
              onFocus={() => {
                setCurrentComponent("doctor_commission_program_name");
              }}
              onChange={(e) => {
                debounceUpdateActors({ name: e.target.value });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ padding: "0 5px 0 0" }}>
                    <Shortcut keys={["Alt", "N"]} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  altUp: "grid_doctor_commission_program",
                  up: "grid_doctor_commission_program",
                  right: "doctor_commission_program_generic_medicine_category",
                  left: "pp_date_picker_doctor_commission_program_end_date",
                  down: "doctor_commission_branch",
                }),
              }}
            />
          </Grid>
        </Grid>
        <Grid sx={{ marginTop: 1 }} container spacing={1} columns={12}>
          <Grid item xs={5}>
            <ChipInputSelect
              id="doctor_commission_program_generic_medicine_category"
              label="Mode"
              ref={genericMedicineCategoryRef}
              key={genericMedicineCategoryRef.current}
              options={genericMedicineCategories}
              value={medicineForm || []}
              getOptionLabel={(option) => option?.label || option?.id || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onFocus={() => {
                setCurrentComponent(
                  "doctor_commission_program_generic_medicine_category"
                );
              }}
              onChange={(event, option) => {
                debounceUpdateActors({ medicineForm: option });
              }}
              openOnFocus={true}
              multiple={true}
              blurOnSelect={false}
              disableCloseOnSelect={true}
              limitTags={2}
              disabled={
                manufacturerGroup?.id === "1ON"
              }
              required={
                manufacturerGroup?.id !== "1ON"
              }
              sx={{
                "& .MuiAutocomplete-input": {
                  minHeight: "40px",
                },
              }}
              markers={JSON.stringify({
                up: "doctor_commission_program_manufacturer_group",
                left: "doctor_commission_program_name",
                right: "doctor_commission_branch",
                down: "doctor_commission_program_generic_medicine_category",
              })}
            />
          </Grid>
          <Grid item xs={5}>
            <ChipInputSelect
              id="doctor_commission_branch"
              key={branchResetKeyRef.current}
              value={branch || []}
              options={branches}
              groupBy={(option) => (option?.code ? "Branches" : "Branch Group")}
              name="branch"
              required={true}
              ref={branchRef}
              getOptionLabel={(option) =>
                `${option?.name || ""} (${option?.code || "BG"})`
              }
              isOptionEqualToValue={(option, value) => {
                return option === value;
              }}
              onFocus={() => {
                setCurrentComponent("doctor_commission_branch");
              }}
              onChange={(event, option) => {
                debounceUpdateActors({ branch: option });
              }}
              openOnFocus={true}
              label="Branches"
              multiple={true}
              blurOnSelect={false}
              disableCloseOnSelect={true}
              limitTags={2}
              sx={{
                "& .MuiAutocomplete-input": {
                  minHeight: "40px",
                },
              }}
              renderGroup={(params) => {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      padding: "5px",
                      gap: "5px",
                    }}
                    key={params.key}
                  >
                    <div
                      style={{
                        height: "30px",
                        width: "100%",
                        background: "#e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        padding: "5px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {params.group}
                    </div>
                    <div>{params.children}</div>
                  </div>
                );
              }}
              renderOption={(props, option, { selected }) => {
                return (
                  <div
                    {...props}
                    style={{
                      display: "flex",
                      height: "40px",
                      width: "100%",
                      padding: "1px 5px",
                      gap: "5px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItem: "center",
                      }}
                    >
                      <Checkbox checked={selected} />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItem: "center",
                        gap: "5px",
                      }}
                    >
                      <div
                        style={{
                          height: "15px",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {option?.name} ({option?.code || "BG"})
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {option?.address}
                      </div>
                    </div>
                  </div>
                );
              }}
              markers={JSON.stringify({
                up: "doctor_commission_program_name",
                left: "doctor_commission_program_generic_medicine_category",
                right: "doctor_commission_branch",
                down: "doctor_commission_branch",
              })}
            />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className={classes["actors-button-wrapper"]}>
              <Button
                label={"Save"}
                ref={saveDoctorCommissionProgramRef}
                color="violet"
                disabled={showLoading}
                endIcon={<Shortcut keys={["Ctrl", "S"]} />}
                onClick={saveClickHandler}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default DoctorCommissionProgramActors;
